import { ContextMenu, Li, Button, Dialog, Loading, Icon } from '@startlibs/components';
import { formatDate, getColor } from '@startlibs/utils';
import { lighten } from 'polished';
import {useToggle, usePopupToggle, willUseSharedCallback} from '@startlibs/core'
import React, { forwardRef } from 'react';
import styled from 'styled-components';

import {
  AttachmentActions,
  AttachmentBox,
  AttachmentDescription,
  AttachmentDetails,
  AttachmentIcon,
  AttachmentInfoContainer,
  DropdownButton,
  DropdownIcon,
  ProgressBar,
  ProgressDetails,
  TextButton,
  UploadedDate,
  UploadingStatus
} from './AttachmentBoxStyles';
import {dicomDateToDate, formatDateNoUTC} from '../utils'
import {useDownload, useViewStudy} from '../utils/downloadFile'
import {useUploader} from '../hooks/useUploader'

const [useRegisterCancel,callCancels] = willUseSharedCallback()

export const DicomBox =
  ({
     study,
     allowDownload,
     disabled,
     hasUnprocessedFiles,
     withoutDelete,
     jwt,
     appJwt,
     onFinishUpload,
     onCancel,
     onFailure,
     retry,
     removeStudyFromQueue,
     removeStudy,
     setSeriesList,
     noCancel,
     isApp,
     dragProvided
  }) => {

  const studyDetailsDialog = useToggle()
  const cancelDialog = useToggle()
  const deleteDialog = useToggle()
  const loading = useToggle()
  const contextMenu = usePopupToggle()

  const uploadingInstances = study.instances.filter(instance => instance.fileToUpload)

  const queuedInstances = study.instances.filter(instance => instance.sourceFile)
  const doneInstances = study.instances.filter(instance => !instance.sourceFile)
  const failedInstances = study.instances.filter(instance => instance.failed)

  const uploaded = doneInstances.length
  const total = study.instances.length

  const progress = uploaded/total

  const [keepProgress,setKeepProgress] = React.useState(uploaded && hasUnprocessedFiles)

  React.useEffect(() => {
    if (hasUnprocessedFiles && !keepProgress) {
      setKeepProgress(true)
    }
    if (!hasUnprocessedFiles && keepProgress) {
      setKeepProgress(false)
    }
  },[uploaded])

  const isFinished = uploaded === total && !keepProgress


  const studyDate = dicomDateToDate(study.studyDate)

  const cancelQueue = () => {
    removeStudyFromQueue(study)
    callCancels()
  }

  const cancelAndRemove = () => {
    cancelQueue()
    return removeStudy(study)
  }

  const retryAll = () => {
    failedInstances.forEach((instance) => retry(instance)())
  }

  const [openViewerLink,viewerIsLoading] = useViewStudy(appJwt,[study.studyUID])
  const [tryDownload,downloadIsLoading,downloadIsStating,downloadFailed] = useDownload(appJwt,{studyUid:study.studyUID})

  const hasOnlyFailedFiles = queuedInstances.length === failedInstances.length && failedInstances.length > 0

  return <AttachmentBox
    ref={dragProvided.innerRef}
    {...dragProvided.draggableProps}
    {...dragProvided.dragHandleProps}
    failedUpload={hasOnlyFailedFiles} isApp={isApp}>
    <AttachmentIcon icon="dicom" css="font-size:44px;"/>
    {
      !isFinished && (hasOnlyFailedFiles ?
      <UploadingStatus>
      {uploaded > 0 && <ProgressDetails>{uploaded}/{total} files successfully uploaded</ProgressDetails>}
      <ProgressDetails failed>
        <a className="link error" onClick={studyDetailsDialog.open}>{failedInstances.length} file upload{failedInstances.length > 1 && 's'} failed</a>
        <a className="link" onClick={retryAll}>Retry{failedInstances.length > 1 && ' all'}</a>
      </ProgressDetails>
      </UploadingStatus>
      :
      <UploadingStatus>
        <ProgressDetails>
          <span>Uploading files...</span>{ !noCancel && <a className="link" onClick={cancelDialog.open}>Cancel</a> }
        </ProgressDetails>
        <ProgressBar progress={progress * 100} completed={isFinished}/>
        {failedInstances.length > 0 ?
          <div className="files-count"><a className="link error" onClick={studyDetailsDialog.open}>{failedInstances.length} file upload{failedInstances.length > 1 && 's'} failed</a> <a className="link" onClick={retryAll}>Retry{failedInstances.length > 1 && ' all'}</a></div>
          :
          <div className="files-count"><a className="link" onClick={studyDetailsDialog.open}>{uploaded}/{total} files uploaded</a></div>
        }
      </UploadingStatus>
    )}
    <AttachmentInfoContainer>
      <AttachmentDescription>
        <b>{study.studyDescription} {!isNaN(studyDate) && `(${formatDate(studyDate,'MM-dd-yyyy')})`} <a className="link" onClick={studyDetailsDialog.open}>Details</a></b>
      </AttachmentDescription>
      {/* <AttachmentDetails><b>Modalities: </b>{study.modalities.join(", ")} ({study.seriesUIDs.length} series)</AttachmentDetails> */}
      <AttachmentDetails><b>Modalities: </b>{study.modalities.join(", ")}</AttachmentDetails>
      <AttachmentDetails><b>Patient: </b>{study.patientName}</AttachmentDetails>
    </AttachmentInfoContainer>
    <AttachmentActions>
      <div className="buttons-wrapper">
        {
          isFinished && !study.isNew && !isApp &&
            <>
              {
                allowDownload && <TextButton isLoading={downloadIsLoading} onClick={tryDownload}>
                {
                  downloadIsLoading ?
                    <><Loading size={15} borderWidth={3}/>Preparing...</>
                    :
                    downloadIsStating ?
                      <><Icon icon="check"/>Started</>
                      :
                      <><Icon icon="download"/>Download</>
                }
              </TextButton>
              }
              <TextButton isLoading={viewerIsLoading} onClick={openViewerLink}><Icon icon="view"/>View</TextButton>
            </>
        }
        {
          isFinished && !disabled && !withoutDelete &&
          <DropdownButton onClick={contextMenu.open}>
            <DropdownIcon icon="arrow-down"/>
            {
              contextMenu.isOpen &&
              <ContextMenu>
                <Li label="Delete medical record" icon="delete" onClick={deleteDialog.open}/>
              </ContextMenu>
            }
          </DropdownButton>
        }
      </div>
      {
        study.uploadDate && isFinished && <UploadedDate>
          Uploaded: {formatDateNoUTC(new Date(study.uploadDate),"MM/dd/yyyy - hh:mm")}
        </UploadedDate>
      }
    </AttachmentActions>
    {
      studyDetailsDialog.isOpen &&
      <StudyDetailsDialog
        closeDialog={studyDetailsDialog.close}
        onCancel={onCancel}
        doneInstances={doneInstances}
        queuedInstances={queuedInstances}
        uploadingInstances={uploadingInstances}
        cancelDialog={cancelDialog}
        study={study}
        retry={retry}
        uploaded={uploaded}
        total={total}
        progress={progress}
        retryAll={failedInstances.length > 0 && retryAll}
        failedInstances={failedInstances}
        isFinished={isFinished}
        hasOnlyFailedFiles={hasOnlyFailedFiles}
      />
    }
    {uploadingInstances.map(instance =>
      <UploadInstance
        jwt={jwt}
        key={instance.instanceUID}
        instance={instance}
        onCancel={onCancel(instance)}
        onFinishUpload={onFinishUpload(instance)}
        onFailure={onFailure(instance)}
        setSeriesList={setSeriesList}
      />
    )}

    {
      cancelDialog.isOpen &&
      <Dialog
        closeDialog={cancelDialog.close}
        title="Cancel DICOM upload"
        footer={<>
          <Button alert isLoading={loading.isOpen} onClick={loading.willWait(cancelAndRemove)}>Delete entire medical record</Button>
          <Button alert onClick={() => { cancelQueue(); cancelDialog.close()}}>Stop remaining uploads only</Button>
        </>}
      >
      <p>Would you like to cancel only the remaining file uploads in queue or also delete all files already uploaded to this medical record?</p>
      </Dialog>
    }
    {
      deleteDialog.isOpen &&
      <Dialog
        closeDialog={deleteDialog.close}
        title="Confirm medical record deletion"
        footer={<>
          <Button onClick={deleteDialog.close}>Cancel</Button>
          <Button alert isLoading={loading.isOpen} onClick={loading.willWait(cancelAndRemove)}>Delete entire medical record</Button>
        </>}
      >
      <p>You are about to delete the following medical record:</p>
      <AttachmentBox css="margin-bottom:1rem;">
        <AttachmentIcon icon="dicom" css="font-size:44px;" className="desktop-only"/>
        <AttachmentInfoContainer>
          <AttachmentDescription><b>{study.studyDescription} {!isNaN(studyDate) && `(${formatDate(studyDate,'MM-dd-yyyy')})`}</b></AttachmentDescription>
          <AttachmentDetails><b>Modalities: </b>{study.modalities.join(", ")}</AttachmentDetails>
          <AttachmentDetails><b>Patient: </b>{study.patientName}</AttachmentDetails>
        </AttachmentInfoContainer>
      </AttachmentBox>
      <p><b>Are you sure you want to delete it?</b> Once confirmed this cannot be undone.</p>
      </Dialog>
    }

  </AttachmentBox>
}

const FilesListHeader = styled.div `
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`
const FilesList = styled.div `
  border-radius: 5px;
  height: 16rem;
  border: 1px solid ${getColor('gray210')};
  overflow: hidden;
  > div {
    overflow: auto;
    height: calc(100% + 1px);
  }
`
const FailedUploads = styled.div `
  color: ${getColor('alert')};
  margin-top: 1rem;
  font-weight: 600;
  margin-bottom: -1rem;
  background: ${props => lighten(0.51, props.theme.colors.alert)};
  border: 1px solid #e09f9f;
  padding: 0.5rem 1rem;
  border-radius: 5px;
  ${Icon} {
    font-size: 18px;
    vertical-align: -4px;
    margin-right: 0.5rem;
  }
`

const StudyDetailsDialog = ({closeDialog, cancelDialog, study, retry, retryAll, uploadingInstances, queuedInstances, doneInstances, studyDate, uploaded, total, progress, failedInstances, isFinished, hasOnlyFailedFiles}) => {
  return <Dialog
    title="DICOM file details"
    closeDialog={closeDialog}
    footer={<Button onClick={closeDialog}>Close</Button>}
  >
    <AttachmentBox css="padding:1rem;margin-bottom:1rem">
      <AttachmentInfoContainer>
        <AttachmentDescription css="font-size:14px;"><b>{study.studyDescription} {!isNaN(studyDate) && `(${formatDate(studyDate,'MM-dd-yyyy')})`}</b></AttachmentDescription>
        <AttachmentDetails>
          <b>Patient:</b> {study.patientName}
        </AttachmentDetails>
        <AttachmentDetails>
          <b>Modalities: </b>{study.modalities.join(", ")}
        </AttachmentDetails>
        <AttachmentDetails>
          <b>Referring physician:</b> {study.referringPhysician}
        </AttachmentDetails>
      </AttachmentInfoContainer>
    </AttachmentBox>
    {study.instances.length > 0 && <>
      {isFinished ?
        <FilesListHeader><div><b>Upload completed</b> ({uploaded}/{total} files uploaded)</div></FilesListHeader>
        : (hasOnlyFailedFiles ?
        <FilesListHeader><div><b>Files uploaded</b> ({uploaded}/{total})</div><a className="link" onClick={cancelDialog.open}>Cancel</a></FilesListHeader>
        :
        <FilesListHeader><div><b>Uploading files</b> ({uploaded}/{total} uploaded)</div><a className="link" onClick={cancelDialog.open}>Cancel upload</a></FilesListHeader>
      )}
      <ProgressBar progress={progress * 100} completed={isFinished} failed={hasOnlyFailedFiles} css="margin: 0.5rem 0;"/>
      <FilesList>
        <div>
        {study.instances.map(instance =>
          <InstanceRow
            key={instance.instanceUID}
            instance={instance}
            retry={retry}
            isUploading={uploadingInstances.indexOf(instance) >= 0}
            isQueued={queuedInstances.indexOf(instance) >= 0}
            isDone={doneInstances.indexOf(instance) >= 0}
          />
        )}
        </div>
      </FilesList>
      {retryAll && <FailedUploads><Icon icon="failure"/>{failedInstances.length} {failedInstances.length > 1 ? "file uploads have failed." : "file upload has failed."} <a className="link" onClick={retryAll}>Retry{failedInstances.length > 1 && ' all'}</a></FailedUploads>}
    </>}
  </Dialog>
}

const InstanceRow = styled(({instance, isUploading, isQueued, retry, className}) => <div className={className}>
  {instance.fileName}
  {
    instance.failed
      ? (
        instance.failed === 'filename'
          ? <span className="failed">Invalid filename. Cannot upload.</span>
          : <span className="failed">Upload failed. <a className="link" onClick={retry(instance)}>Retry</a></span>
      )
      : isUploading
      ? <span><b><Loading size={12} borderWidth={2}/>Uploading</b></span>
      : isQueued
      ? <span>Waiting</span>
      : <span>Uploaded</span>
  }
</div>)`
  padding: 1rem;
  border-bottom: 1px solid ${getColor('gray210')};
  display: flex;
  justify-content: space-between;
  span {
    flex-shrink: 0;
    margin-left: 1.5rem;
  }
  .failed {
    color: ${getColor('alert')};
  }
  ${Loading} {
    display: inline-block;
    margin-right: 4px;
    vertical-align: -1px;
  }
`

const UploadInstance = forwardRef(({instance, jwt, onFinishUpload, setSeriesList, onCancel, onFailure}, ref) => {


  // const onCancel = console.log
  // const onFailure = console.log

  const params = {instanceUID: instance.instanceUID, studyUID: instance.studyUID, fileType: "DICOM"}


  const [progress, cancel] = useUploader(instance.fileToUpload, {
    jwt,
    params,
    onSuccess:onFinishUpload,
    onCancel,
    onFailure
  })

  useRegisterCancel((i) => {
    cancel()
  })

  return null

})
